import React, { useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Colors } from 'library';
import {
    RCard,
    RErrorText,
    RFieldGroup,
    RModalFooter,
    RPillButton,
    RStack,
    RText,
    RTextInput,
} from 'recruit/components';
import { createSetupIntent, savePaymentMethod } from './api';
import { readPaymentMethodId } from './stripeHelpers';
import { PaymentMethod } from './types';

interface AddCardFormProps {
    onSuccess: (method: PaymentMethod) => void;
    onCancel?: () => void;
    submitLabel?: string;
    cancelLabel?: string;
}

export function AddCardForm({
    onSuccess,
    onCancel,
    submitLabel = 'Add Card',
    cancelLabel = 'Cancel',
}: AddCardFormProps) {
    const stripe = useStripe();
    const elements = useElements();
    const [cardholderName, setCardholderName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function saveCard() {
        if (!stripe || !elements) return;
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) return;

        setSubmitting(true);
        setError(null);

        try {
            const { client_secret } = await createSetupIntent('card');
            const result = await stripe.confirmCardSetup(client_secret, {
                payment_method: {
                    card: cardElement,
                    billing_details: { name: cardholderName || undefined },
                },
            });
            if (result.error) {
                setError(result.error.message ?? 'Could not save card.');
                setSubmitting(false);
                return;
            }
            const paymentMethodId = readPaymentMethodId(result);
            if (!paymentMethodId) {
                setError('Stripe did not return a payment method id.');
                setSubmitting(false);
                return;
            }
            const saved = await savePaymentMethod(paymentMethodId, result.setupIntent?.status);
            setSubmitting(false);
            onSuccess(saved);
        } catch {
            setError('Could not save card. Please try again.');
            setSubmitting(false);
        }
    }

    return (
        <RStack gap="md">
            <RFieldGroup label="Cardholder Name">
                <RTextInput
                    value={cardholderName}
                    onChange={setCardholderName}
                    placeholder="Name on card"
                    fullWidth
                />
            </RFieldGroup>
            <RFieldGroup label="Card Details">
                <RCard tone="muted" padding="xs">
                    <CardElement
                        options={{
                            hidePostalCode: false,
                            style: {
                                base: {
                                    fontSize: '15px',
                                    color: Colors.darkNavy,
                                    '::placeholder': { color: Colors.mediumGrey },
                                },
                                invalid: { color: Colors.mediumRed },
                            },
                        }}
                    />
                </RCard>
            </RFieldGroup>
            {error && <RErrorText>{error}</RErrorText>}
            {!stripe && <RText tone="secondary">Loading Stripe…</RText>}
            <RModalFooter>
                {onCancel && (
                    <RPillButton variant="secondary" onClick={onCancel} disabled={submitting}>
                        {cancelLabel}
                    </RPillButton>
                )}
                <RPillButton variant="primary" onClick={saveCard} disabled={!stripe || submitting}>
                    {submitting ? 'Saving…' : submitLabel}
                </RPillButton>
            </RModalFooter>
        </RStack>
    );
}
